import Experience, { ExperienceSource } from 'model/IExperience';
import { ParseReturn } from 'model/UrlParsing';
import { SortType, typeToParams } from 'modules/search/model/sorting';
import req from 'utils/req';
import { z } from 'zod';
import { EXP_SEARCH_PAGE_SIZE } from '~/constants';

import slugify from '../../utils/slugify';

export function fetchSearchWithOther(params: URLSearchParams) {
  const searchParams = new URLSearchParams(params);
  searchParams.set('fieldset', 'reactsearch');
  const url = `${URL_API_HOST}/api/v1/experience/searchWithOthers?${searchParams.toString()}`;
  return req(url).get();
}

export function searchWithOthers(
  filters: ParseReturn,
  sort: SortType,
  currency: string,
  options: { pageSize?: number } = {},
) {
  const params = filters.activeFilters.reduce(
    (acc, curr) => {
      if (curr.value) {
        if (curr.uid === 'family-friendly') {
          acc.set('other.familyfriendly', 'true');
        } else {
          acc.set(
            curr.searchWithOthersQueryName || curr.queryName,
            decodeURIComponent(curr?.value ?? ''),
          );
        }
      } else if (curr.subOptions.length > 0) {
        // only one single activityType.activity is supported. When multiple are sent (we have a maximum of 2), don't send them at all.
        if (
          curr.uid === 'activityType.activity' &&
          curr.subOptions.length === 2
        ) {
          return acc;
        }

        curr.subOptions.forEach((subOptions) => {
          acc.append(
            curr.uid,
            decodeURIComponent(
              subOptions.queryValue || subOptions?.value || '',
            ),
          );
        });
      }
      return acc;
    },
    new URLSearchParams({ page: filters.page.toString() }),
  );

  if (filters.country) {
    params.set(
      'location.country',
      decodeURIComponent(filters.country.replace(/-/g, ' ')),
    );
  }

  if (filters.city) {
    params.set(
      'location.city',
      decodeURIComponent(filters.city.replace(/-/g, ' ')),
    );
  }

  if (filters.lang) {
    params.set('lang', filters.lang);
  }

  params.set('pagesize', `${options.pageSize || EXP_SEARCH_PAGE_SIZE}`);
  if (!currency) {
    console.error('No currency provided');
  }
  params.set('currency', currency);

  if (
    filters.activeFilters &&
    filters.activeFilters.some((f) => f.uid === 'date.from' && f?.value) &&
    !filters.activeFilters.some((f) => f.uid === 'date.to' && f?.value)
  ) {
    const dateTo = filters.activeFilters.find(
      (f) => f.uid === 'date.from',
    )?.value;
    params.set('date.to', dateTo);
  }

  if (sort) {
    typeToParams[sort].forEach(({ name, value }) => params.set(name, value));
  }

  return fetchSearchWithOther(params);
}

export function fetchFamilyFriendlyExperiences(country, city, langCode) {
  return req(
    `${URL_API_HOST}/api/v3/experiences/list/family_friendly_${slugify(
      country,
    )}_${slugify(city)}?include=experience&per_page=3&lang=${langCode}`,
  ).get();
}

export function fetchExperience(
  experienceId: string,
  langCode: string,
  currency: string,
  addLiveParam?: boolean,
): Promise<ExperienceSource> {
  const searchParams = new URLSearchParams({
    lang: langCode,
  });

  if (!currency) {
    console.error('No currency provided');
  }
  searchParams.set('currency', currency);

  if (addLiveParam) {
    searchParams.append('live', 'true');
  }

  const url = `${URL_API_HOST}/api/v3/experience/${experienceId}?${searchParams.toString()}`;
  return req(url).get();
}

export function fetchAvailableTimes({
  experienceId,
  date,
  hostId,
}: {
  experienceId?: string;
  date?: string;
  hostId?: string;
}): Promise<{ [key: string]: string[] }> {
  if (!experienceId || !date) {
    return Promise.resolve({});
  }
  if (hostId) {
    return req(
      `${URL_API_HOST}/api/v2/agenda/experience/times/${experienceId}/${date}/${hostId}`,
    ).get();
  }
  return req(
    `${URL_API_HOST}/api/v2/agenda/experience/times/${experienceId}/${date}`,
  ).get();
}

export function fetchHostExperiences(hostId) {
  return req(`${URL_API_HOST}/api/v1/experience/host/${hostId}`).get();
}

const AvailabilityPerHostSchema = z.object({
  availability: z.record(z.string().uuid(), z.array(z.string())),
});
export type AvailabilityPerHost = z.infer<typeof AvailabilityPerHostSchema>;

export async function fetchAvailableDatesPerHost(experienceId: string) {
  const response = await req(
    `${URL_API_HOST}/api/v2/agenda/experience/availability-by-host/${experienceId}`,
  ).get();

  const result = AvailabilityPerHostSchema.safeParse(response);
  if (!result.success) {
    console.error(
      'Error fetching availability per host, but still returning data',
      result.error,
    );
    return response;
  }

  return result.data;
}

export function fetchHostExperiencesPaginated(
  prettyUrl,
  currency = 'EUR',
): Promise<Experience[]> {
  const searchParams = new URLSearchParams({
    page: '1',
    pageSize: '30',
    currency,
  });
  return req(
    `${URL_API_HOST}/api/v1/host/experiences/${prettyUrl}?${searchParams.toString()}`,
  ).get();
}
